import React, { useEffect, useRef } from 'react';
import type { SmsMessage } from '../../../hooks/support/useDialpadSms';

export interface MessageThreadProps {
  /** Messages to display, sorted oldest-first */
  messages: readonly SmsMessage[];
  /** Loading state */
  isLoading: boolean;
  /** Compact styling */
  compact?: boolean;
  /**
   * Host already supplies the card; use its 16px horizontal gutter so the
   * bubbles line up with the bands above and below them.
   */
  flush?: boolean;
}

/**
 * Format a Firestore Timestamp or ISO string for display.
 * Shows time only for today, date + time for older messages.
 */
function formatTimestamp(timestamp: string | { toDate: () => Date } | undefined): string {
  if (!timestamp) return '';

  // Handle string timestamps (ISO format)
  const date = typeof timestamp === 'string'
    ? new Date(timestamp)
    : timestamp.toDate();
  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  if (isToday) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  const isSameYear = date.getFullYear() === now.getFullYear();

  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    ...(isSameYear ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * MessageThread - Displays SMS messages in a chat-style thread.
 *
 * - Inbound messages: left-aligned, gray background
 * - Outbound messages: right-aligned, blue background
 * - Auto-scrolls to bottom on new messages
 */
export function MessageThread({
  messages,
  isLoading,
  compact = false,
  flush = false,
}: MessageThreadProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(messages.length);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messages.length !== prevCountRef.current) {
      prevCountRef.current = messages.length;
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages.length]);

  // Scroll to bottom on initial load
  useEffect(() => {
    if (!isLoading && messages.length > 0) {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'auto',
      });
    }
  }, [isLoading, messages.length]);

  if (isLoading) {
    return (
      <div className={`flex-1 overflow-y-auto ${compact ? 'p-2' : 'p-4'}`}>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`
                  rounded-2xl animate-pulse bg-slate-200
                  ${compact ? 'h-8 w-40' : 'h-10 w-48'}
                `}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div
        className={`
          flex-1 flex items-center justify-center text-slate-400
          ${flush ? 'px-4 py-6 text-xs' : compact ? 'p-2 text-xs' : 'p-4 text-sm'}
        `}
      >
        No messages yet
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className={`flex-1 overflow-y-auto ${
        flush ? 'px-4 py-3 space-y-1.5' : compact ? 'p-2 space-y-1.5' : 'p-4 space-y-3'
      }`}
    >
      {messages.map((message) => {
        const isOutbound = message.direction === 'outbound';

        return (
          <div
            key={message.id}
            className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`
                max-w-[75%] rounded-2xl
                ${compact ? 'px-3 py-1.5' : 'px-4 py-2.5'}
                ${
                  isOutbound
                    ? 'bg-navy-600 text-white rounded-br-sm'
                    : 'bg-slate-100 text-slate-900 rounded-bl-sm'
                }
              `}
            >
              <p
                className={`whitespace-pre-wrap break-words ${
                  compact ? 'text-xs' : 'text-sm'
                }`}
              >
                {message.text}
              </p>
              <div
                className={`
                  mt-1 flex items-center gap-1
                  ${
                    isOutbound
                      ? 'text-navy-200 justify-end'
                      : 'text-slate-400 justify-start'
                  }
                  ${compact ? 'text-[10px]' : 'text-xs'}
                `}
              >
                <span>{formatTimestamp(message.timestamp)}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
